import {
  type ColumnDef,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  type CSSProperties,
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { Portal } from '@air/primitive-portal';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import {
  formatActiveTableColumns,
  GENERIC_BOARD_ID,
  getColumnWidthMap,
  getColumnWidthVariable,
  getInitialColumnWidth,
  getMaxColumnWidth,
  getMinColumnWidth,
  getTableWidthVariable,
  getTableWidthVariableValue,
  getWidthsFromLocalStorage,
  makeUpdaterForColumnSizing,
  saveWidthsToLocalStorageThrottled,
  setTableDimensionsAsCSSVariables,
} from '~/components/TableView/tableColumnWidthsUtils';
import { type ActiveTableColumn, type AirTableRow } from '~/components/TableView/types';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { activeTableColumnsSelector } from '~/store/configViews/selectors';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type TableViewContextValue = {
  getHeaderResizeHandle: (activeTableColumn: ActiveTableColumn) => ReactNode | null;
  getColumnWidthStyle: (activeTableColumn: ActiveTableColumn) => CSSProperties;
  getTableWidthStyle: () => CSSProperties;
  getTableWidthAsNumber: () => number;
};

const defaultValue: TableViewContextValue = {
  getHeaderResizeHandle: () => null,
  getColumnWidthStyle: () => ({}),
  getTableWidthStyle: () => ({}),
  getTableWidthAsNumber: () => 0,
};

export const TableViewContext = createContext<TableViewContextValue>(defaultValue);

type TableViewProviderProps = {
  children: ReactNode;
};

export const TableViewProvider = memo(({ children }: TableViewProviderProps) => {
  // Board ID is `generic` when the table doesn't related to a board, e.g. "All assets & files"
  const boardId = useURLBoardIdSelector() ?? GENERIC_BOARD_ID;

  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({
    startOffset: 0,
    startSize: 0,
    deltaOffset: 0,
    deltaPercentage: 0,
    isResizingColumn: false,
    columnSizingStart: [],
  });
  const activeTableColumns = useSelector(activeTableColumnsSelector);
  const tableColumns = useMemo(() => formatActiveTableColumns(activeTableColumns), [activeTableColumns]);

  // Tanstack Table FAQ strongly recommends stable data and columns
  // Reference: https://tanstack.com/table/latest/docs/faq
  const data = useMemo(() => {
    return [];
  }, []);
  const columns = useMemo<ColumnDef<AirTableRow>[]>(() => {
    return tableColumns.map((column) => ({
      accessorKey: column.fieldId,
      minSize: getMinColumnWidth(column.fieldId),
      maxSize: getMaxColumnWidth(column.fieldId),
    }));
  }, [tableColumns]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: (updater) => {
      setColumnSizingInfo(updater);
      const newColumnSizingInfo = typeof updater === 'function' ? updater(columnSizingInfo) : updater;

      const columnIsResizing = !!(
        newColumnSizingInfo.isResizingColumn &&
        newColumnSizingInfo.startSize &&
        newColumnSizingInfo.deltaOffset
      );

      const newColumnSizing = {
        ...Object.fromEntries(
          Object.entries(columnSizing).map(([key, value]) => [
            key,
            Math.min(Math.max(value, getMinColumnWidth(key)), getMaxColumnWidth(key)),
          ]),
        ),
        ...(!!(
          newColumnSizingInfo.isResizingColumn &&
          newColumnSizingInfo.startSize &&
          newColumnSizingInfo.deltaOffset
        ) && {
          [newColumnSizingInfo.isResizingColumn]: Math.min(
            Math.max(
              newColumnSizingInfo.startSize + newColumnSizingInfo.deltaOffset,
              getMinColumnWidth(newColumnSizingInfo.isResizingColumn),
            ),
            getMaxColumnWidth(newColumnSizingInfo.isResizingColumn),
          ),
        }),
      };

      if (columnIsResizing) {
        const columnWidthMap = getColumnWidthMap(tableColumns, newColumnSizing);

        table.setColumnSizing(makeUpdaterForColumnSizing(Object.fromEntries(columnWidthMap.entries())));
        setTableDimensionsAsCSSVariables(boardId, tableColumns, columnWidthMap, responsiveHorizontalPadding);
        saveWidthsToLocalStorageThrottled(boardId, Object.fromEntries(columnWidthMap));
      }
    },
    initialState: {
      columnSizing,
      columnSizingInfo,
    },
    state: {
      columnSizing,
      columnSizingInfo,
    },
  });

  // Purpose: Load column widths from localStorage when boardId/columns change
  useEffect(() => {
    const savedColumnWidths = getWidthsFromLocalStorage(boardId);
    const currentWidths: ColumnSizingState = {};
    for (const column of tableColumns) {
      currentWidths[column.fieldId] = savedColumnWidths[column.fieldId] || getInitialColumnWidth(column.fieldId);
    }

    const columnWidthMap = getColumnWidthMap(tableColumns, currentWidths);

    table.setColumnSizing(makeUpdaterForColumnSizing(Object.fromEntries(columnWidthMap.entries())));
    setTableDimensionsAsCSSVariables(boardId, tableColumns, columnWidthMap, responsiveHorizontalPadding);
  }, [boardId, tableColumns, table, responsiveHorizontalPadding]);

  // Purpose: Render a resize handle for the header of the column
  const getHeaderResizeHandle = useCallback(
    (activeTableColumn: ActiveTableColumn) => {
      const header = table.getFlatHeaders().find((header) => header.id === activeTableColumn.fieldName);

      if (!header) {
        return null;
      }

      const isResizing = header.column.getIsResizing();

      return (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          data-draggable="true"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          // biome-ignore lint/plugin: these resize-handle classes are static and split across lines so the comments stay next to each group
          className={cn(
            // base
            'group absolute',
            'top-0 right-0 bottom-0 h-full w-3 cursor-col-resize touch-none select-none',
          )}
        >
          <div
            className={cn(
              // base
              'absolute top-3 bottom-3 left-[5px] z-0 w-px bg-gray-4',
              // hover
              'group-hover:top-0 group-hover:h-full',
              isResizing && 'top-0 h-full',
            )}
          />
          <div
            className={cn(
              // base
              'absolute top-0 left-[4px] z-1 h-full w-[3px] ads:bg-primary bg-blue-9 opacity-0',
              // hover
              'transition-opacity duration-300 group-hover:opacity-100',
              isResizing && 'opacity-100',
            )}
          />
          {/* prevent the mouse cursor from flickering between the default and the resize cursor as the user resizes the column */}
          {isResizing && (
            <Portal>
              <div className="fixed inset-0 z-[9999] size-full cursor-col-resize bg-transparent" />
            </Portal>
          )}
        </button>
      );
    },
    [table],
  );

  // Purpose: Render a style for the table width
  const getTableWidthStyle = useCallback((): CSSProperties => {
    const tableWidth = getTableWidthVariableValue(boardId);

    return {
      minWidth: tableWidth ? getTableWidthVariable(boardId) : '100%',
    };
  }, [boardId]);

  const getTableWidthAsNumber = useCallback((): number => {
    const tableWidth = getTableWidthVariableValue(boardId);

    return tableWidth ? parseInt(tableWidth, 10) : 0;
  }, [boardId]);

  // Purpose: Render a style for the column width
  const getColumnWidthStyle = useCallback(
    (activeTableColumn: ActiveTableColumn): CSSProperties => {
      const columnVariable = getColumnWidthVariable(boardId, activeTableColumn.fieldName);

      return {
        flex: `0 1 ${columnVariable}`,
        minWidth: columnVariable,
      };
    },
    [boardId],
  );

  const providerValue = useMemo(
    () => ({
      getHeaderResizeHandle,
      getColumnWidthStyle,
      getTableWidthStyle,
      getTableWidthAsNumber,
    }),
    [getHeaderResizeHandle, getColumnWidthStyle, getTableWidthStyle, getTableWidthAsNumber],
  );

  return <TableViewContext.Provider value={providerValue}>{children}</TableViewContext.Provider>;
});

TableViewProvider.displayName = 'TableViewProvider';

export const useTableView = () => {
  const context = useContext(TableViewContext);

  if (!context) {
    const error = new Error('useTableView must be used within a TableViewProvider');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error.message,
      });
    }
  }

  return context;
};
